import { useCallback, useState } from 'react';

import type { RegistryOrder, RegistryOrderCloseRequest } from '@/types/registry-documents';

/**
 * The "Close order" dialog of a surface that lists orders: which order it
 * is open for, and what its confirm does.
 *
 * One dialog per surface rather than one per row, so it outlives a row
 * that leaves the list the moment its order is closed. The order stays
 * set after the dialog is dismissed: its exit transition still shows it.
 *
 * `close` is the surface's own action (it re-reads the list); a failure
 * is re-thrown for the dialog to show, and leaves it open.
 */
export function useRegistryOrderCloseDialog<T extends RegistryOrder>(
  close: (orderId: string, request: RegistryOrderCloseRequest) => Promise<unknown>,
  onClosed?: (order: T) => void
) {
  const [order, setOrder] = useState<T | null>(null);
  const [opened, setOpened] = useState(false);

  const open = useCallback((target: T) => {
    setOrder(target);
    setOpened(true);
  }, []);

  const dismiss = useCallback(() => setOpened(false), []);

  const confirm = useCallback(
    async (request: RegistryOrderCloseRequest) => {
      if (!order) {
        return;
      }
      await close(order.id, request);
      setOpened(false);
      onClosed?.(order);
    },
    [order, close, onClosed]
  );

  return {
    /** Open the dialog for an order. */
    open,
    /** Spread onto `RegistryOrderCloseModal`. */
    modalProps: { opened, order, onClose: dismiss, onConfirm: confirm },
  };
}
